import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw, Package, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';
import SEO from '../components/SEO';
import axios from 'axios';

const storeApi = axios.create({
  baseURL: (import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '/api/store'),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

interface Purchase {
  id: number;
  product_key: string;
  product_name: string;
  amount_paid: string | null;
  purchased_at: string;
  download_url: string | null;
  downloads_remaining: number;
  token_expired: boolean;
}

export default function MyPurchasesPage() {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<number | null>(null);
  const { token } = useAuthStore();

  const downloadBase = (import.meta.env.VITE_API_URL || 'https://residualvault.com/api/v1').replace('/api/v1', '');

  const loadPurchases = async () => {
    try {
      const res = await storeApi.get('/my-purchases', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPurchases(res.data.purchases);
    } catch {
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPurchases(); }, []);

  const handleRefreshToken = async (purchaseId: number) => {
    setRefreshing(purchaseId);
    try {
      await storeApi.post('/refresh-token', { purchase_id: purchaseId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Download link refreshed!');
      loadPurchases();
    } catch {
      toast.error('Failed to refresh download link');
    } finally {
      setRefreshing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEO title="My Purchases — ResidualVault" description="Download your purchased ResidualVault education manuals." canonical="https://residualvault.com/store/my-purchases" />
      <div className="min-h-screen py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2">My Purchases</h1>
          <p className="text-slate-400 mb-8">Download your purchased manuals</p>

          {purchases.length === 0 ? (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-12 text-center">
              <Package className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">No purchases yet</h2>
              <p className="text-slate-400 mb-6">Visit our store to explore our education manuals.</p>
              <Link to="/store" className="inline-flex items-center gap-2 py-3 px-6 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-400 transition-all">
                Browse Store
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {purchases.map(p => (
                <div key={p.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">{p.product_name}</h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                      {p.amount_paid && <span>${p.amount_paid}</span>}
                      <span>{new Date(p.purchased_at).toLocaleDateString()}</span>
                      <span>{p.downloads_remaining} downloads left</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.token_expired ? (
                      <button
                        onClick={() => handleRefreshToken(p.id)}
                        disabled={refreshing === p.id}
                        className="flex items-center gap-2 py-2 px-4 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 transition-all text-sm disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${refreshing === p.id ? 'animate-spin' : ''}`} />
                        Refresh Link
                      </button>
                    ) : p.download_url ? (
                      <a
                        href={`${downloadBase}${p.download_url}`}
                        className="flex items-center gap-2 py-2 px-4 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/30 transition-all text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    ) : (
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <AlertCircle className="w-4 h-4" /> No downloads remaining
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
